import React, { useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Brand from './Brand';
import { publicDataFetch } from '../api';
import './Contact.css';

const {
  FiArrowUpRight,
  FiCheck,
  FiMail,
  FiLinkedin,
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiClock,
} = FiIcons;

const services = ['Custom App Development', 'Booking App Demo'];

const formatDate = (date) => date.toISOString().split('T')[0];

const getWeekdays = () => {
  const dates = [];
  const today = new Date();

  for (let offset = 1; offset <= 14 && dates.length < 5; offset += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + offset);
    if (date.getDay() !== 0 && date.getDay() !== 6) dates.push(date);
  }

  return dates;
};

const getTimeSlots = () => {
  const slots = [];

  [10, 11, 12, 14, 15, 16, 17].forEach((hour) => {
    [0, 30].forEach((minute) => {
      if (hour === 12 && minute === 30) return;
      slots.push(`${String(hour).padStart(2, '0')}:${minute === 0 ? '00' : '30'}`);
    });
  });

  return slots;
};

const displayTime = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

function BookingForm() {
  const weekdays = useMemo(getWeekdays, []);
  const timeSlots = useMemo(getTimeSlots, []);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState({
    name: '',
    business: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    time: '',
  });

  const updateBooking = (field, value) => {
    setBooking((current) => ({ ...current, [field]: value }));
  };

  const selectedDate = weekdays.find((date) => formatDate(date) === booking.date);

  const submitBooking = async (event) => {
    event.preventDefault();
    setBookingError('');
    setSubmitting(true);

    const response = await publicDataFetch('create/bookings', {
      method: 'POST',
      body: JSON.stringify({
        name: booking.name,
        business: booking.business,
        phone: booking.phone,
        email: booking.email,
        service: booking.service,
        booking_date: booking.date,
        booking_time: booking.time,
        status: 'pending',
      }),
    });

    setSubmitting(false);

    if (response?.status !== 'success') {
      setBookingError(response?.error || 'We could not save your booking request. Please try again.');
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="booking-success booking-step">
        <span className="booking-success-icon"><SafeIcon icon={FiCheck} /></span>
        <span className="booking-kicker">Booking request received</span>
        <h3>Thank you, {booking.name.split(' ')[0]}.</h3>
        <p className="booking-success-message">Your Information has been received. You shall hear from us very soon</p>
        <div className="booking-summary">
          <div><span>Service</span><strong>{booking.service}</strong></div>
          <div><span>Date</span><strong>{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong></div>
          <div><span>Time</span><strong>{displayTime(booking.time)} · 30 minutes</strong></div>
          <div><span>Contact</span><strong>{booking.email}</strong></div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="contact-form booking-form"
      onSubmit={async (event) => {
        event.preventDefault();

        if (step === 1) {
          setStep(2);
          return;
        }

        await submitBooking(event);
      }}
    >
      {step === 1 && (
        <div className="booking-step">
          <div className="booking-fields">
            <label>Full name<input required value={booking.name} onChange={(event) => updateBooking('name', event.target.value)} placeholder="Your name" /></label>
            <label>Business name<input required value={booking.business} onChange={(event) => updateBooking('business', event.target.value)} placeholder="Company or brand name" /></label>
            <label>Contact number<input required type="tel" value={booking.phone} onChange={(event) => updateBooking('phone', event.target.value)} placeholder="+91 00000 00000" /></label>
            <label>Email ID<input required type="email" value={booking.email} onChange={(event) => updateBooking('email', event.target.value)} placeholder="you@company.com" /></label>
          </div>
          <button className="booking-button" type="submit">Continue to availability <SafeIcon icon={FiArrowRight} /></button>
        </div>
      )}

      {step === 2 && (
        <div className="booking-step">
          <fieldset className="booking-choice-group">
            <legend>Services you are looking at</legend>
            {services.map((service) => (
              <label className={booking.service === service ? 'booking-choice selected' : 'booking-choice'} key={service}>
                <input required type="radio" name="service" value={service} checked={booking.service === service} onChange={(event) => updateBooking('service', event.target.value)} />
                <span>{service}</span>
                {booking.service === service && <SafeIcon icon={FiCheck} />}
              </label>
            ))}
          </fieldset>

          <fieldset className="booking-choice-group">
            <legend><SafeIcon icon={FiCalendar} /> Choose a date</legend>
            <div className="booking-date-grid">
              {weekdays.map((date) => {
                const value = formatDate(date);
                return (
                  <label className={booking.date === value ? 'booking-date selected' : 'booking-date'} key={value}>
                    <input required type="radio" name="date" value={value} checked={booking.date === value} onChange={(event) => updateBooking('date', event.target.value)} />
                    <small>{date.toLocaleDateString('en-US', { weekday: 'short' })}</small>
                    <strong>{date.getDate()}</strong>
                    <span>{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="booking-choice-group">
            <legend><SafeIcon icon={FiClock} /> Show time · 30 minutes</legend>
            <div className="booking-time-grid">
              {timeSlots.map((time) => (
                <label className={booking.time === time ? 'booking-time selected' : 'booking-time'} key={time}>
                  <input required type="radio" name="time" value={time} checked={booking.time === time} onChange={(event) => updateBooking('time', event.target.value)} />
                  {displayTime(time)}
                </label>
              ))}
            </div>
          </fieldset>

          {bookingError && <p role="alert">{bookingError}</p>}

          <div className="booking-actions">
            <button className="booking-back" type="button" onClick={() => setStep(1)}><SafeIcon icon={FiArrowLeft} /> Back</button>
            <button className="booking-button" type="submit" disabled={submitting}>
              {submitting ? 'Saving booking...' : 'Confirm booking'} <SafeIcon icon={submitting ? FiClock : FiCheck} />
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

function Contact() {
  return (
    <section className="contact-section panel-section" id="contact">
      <div className="contact-main">
        <span className="section-index">04 / Contact</span>
        <h2>Let’s turn your next <em>growth idea</em> into momentum.</h2>
        <p>
          Share a little about your goals, and our team will get back to you
          with a thoughtful next step.
        </p>

        <div className="contact-form-shell">
          <div className="contact-form-intro">
            <div>
              <span className="contact-form-kicker">Start a conversation</span>
              <h3>Book a Demo call with us</h3>
            </div>
            <span className="contact-form-status">
              <i />
              Typically replies within 1 business day
            </span>
          </div>

          <BookingForm />

          <div className="contact-form-note">
            <span className="contact-note-icon">
              <SafeIcon icon={FiCheck} />
            </span>
            <span>Your information stays private and is only used to respond to your enquiry.</span>
          </div>
        </div>

        <a className="contact-link" href="mailto:office@growthsparrow.com">
          <span><SafeIcon icon={FiMail} /></span>
          office@growthsparrow.com
          <SafeIcon icon={FiArrowUpRight} />
        </a>
      </div>

      <footer>
        <Brand />
        <div className="footer-links">
          <a href="#services">Features</a>
          <a href="#about">Who it’s for</a>
          <a href="#testimonial">Stories</a>
          <a href="/privacy-policy">Privacy</a>
          <a href="/terms-of-use">Terms</a>
        </div>
        <div className="social-links">
          <a
            href="https://www.linkedin.com/company/growthsparrow/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <SafeIcon icon={FiLinkedin} />
          </a>
        </div>
        <span>© 2025 Growth Sparrow</span>
      </footer>
    </section>
  );
}

export default Contact;